export const resolveSchoolLevelTemplateOverrideForSle = ({
    view,
    selectedSchoolLocationKey,
    urlLocationKey,
    schoolTemplatesByLocation
}) => {
    const groupsOverride =
        view === "GROUPS" && selectedSchoolLocationKey ? schoolTemplatesByLocation?.[selectedSchoolLocationKey] : null;
    const adminOverride = urlLocationKey ? schoolTemplatesByLocation?.[urlLocationKey] : null;
    return groupsOverride || adminOverride || null;
};

export const makeSleKey = (sleId) => `SLE:${sleId}`;
export const makeSleChildKey = (sleChildId) => `SLEC:${sleChildId}`;

export const parseSleUiKey = (uiKey) => {
    if (uiKey.startsWith("SLEC:")) {
        return { sleChildId: Number(uiKey.slice(5)), sleId: null };
    }
    if (uiKey.startsWith("SLE:")) {
        return { sleId: Number(uiKey.slice(4)), sleChildId: null };
    }
    return { sleId: null, sleChildId: null };
};

export const normalizeSleRatingForUi = (rating) => {
    if (!rating) return "";
    return rating === "DOES_NOT_MEET" ? "DOES NOT MEET" : rating;
};

export const normalizeSleRatingForApi = (rating) => {
    if (rating == null) return null; // used for delete
    if (rating === "DOES NOT MEET") return "DOES_NOT_MEET";
    return rating;
};

const getGradingProfileKey = (selectedEpr, selectedGrade, gradingProfiles, effectiveTimePeriodDto) => {
    let key = selectedEpr?.key;

    if (!key && selectedGrade) {
        const match = gradingProfiles.find((gp) => {
            const isGradeMatch = gp.gradeName === selectedGrade;

            // Normalize both to strings for a safe comparison
            const dtoKeyStr = String(effectiveTimePeriodDto?.key ?? "");
            const gpTimeStr = String(gp.timePeriod ?? "");
            const isTimeMatch = dtoKeyStr === gpTimeStr;

            return isGradeMatch && isTimeMatch;
        });

        if (match) key = match.key;
    }

    // Ensure we return null instead of undefined if key is missing
    return key ? Number(key) : null;
};

export function buildSleGroupInfoDto({
    sleSelected,
    view,
    selectedGroups = [],
    selectedEpr,
    selectedGrade,
    gradingProfiles = [],
    teacherGuid,
    profileLocationKey: providedKey,
    effectiveTimePeriodDto
}) {
    // 1. Guard Clause
    if (!sleSelected || view !== "GROUPS" || !selectedGroups.length) return null;

    // 2. Data Extraction (using the helper)
    const teacherGradingProfile = getGradingProfileKey(
        selectedEpr,
        selectedGrade,
        gradingProfiles,
        effectiveTimePeriodDto
    );

    const teacherHomeroomGroups = [];
    const sharedGroupInfos = [];
    let derivedLocation = providedKey || null;

    // 3. Process Groups
    for (const group of selectedGroups) {
        if (!group) continue;

        if (group.value?.startsWith?.("homeroom-")) {
            const id = group.value.split("-")[1];
            if (id) teacherHomeroomGroups.push({ groupId: Number(id) });
        } else if (group.groupType === "SHARED") {
            const { sharedGradingProfile: sgp, sharedMeta: meta } = group;

            if (sgp && meta?.sharedGroupId) {
                sharedGroupInfos.push({
                    sharedGradingProfile: Number(sgp),
                    sharedGroupId: Number(meta.sharedGroupId)
                });

                // Only capture location if we don't have one yet
                if (!derivedLocation && meta.locationKey) {
                    derivedLocation = meta.locationKey;
                }
            }
        }
    }

    // 4. Final Validation
    if (!teacherHomeroomGroups.length && !sharedGroupInfos.length) return null;

    return {
        teacherGradingProfile,
        teacherGuid: teacherGuid ?? null,
        teacherHomeroomGroups,
        sharedGroupInfos,
        profileLocationKey: derivedLocation
    };
}

/**
 * Flatten group SLE rows into a unique student list for GROUPS mode,
 * Prioritize "EDIT" over "VIEW" if a student is in multiple groups with diff permissions.
 *
 * @param {Array<Object>} groupRows
 * @returns {Array<Object>}
 */
export function flattenGroupSleStudents(groupRows = [], sharedGradingProfileDtos) {
    const map = new Map();

    for (const group of groupRows) {
        const groupGradingProfile = sharedGradingProfileDtos?.find(
            (sharedDto) => Number(sharedDto.key) === Number(group.gradingProfileId)
        );
        for (const stu of group.groupStudentSles || []) {
            const gpsId = Number(stu.gradingProfileStudentId);
            const existing = map.get(gpsId);

            if (!existing) {
                map.set(gpsId, {
                    ...stu,
                    groupName: group.groupName,
                    groupHomeroomTeacher: group.teacherName,
                    isShared: group.permission === "EDIT" || group.permission === "VIEW",
                    groupPermission: group.permission,
                    groupType: group.groupType,
                    groupId: group.groupId,
                    groupGradingProfileStatus: groupGradingProfile?.status,
                    key: gpsId,
                    gradingProfileStudentId: gpsId,
                    gradingProfileId: group.gradingProfileId
                });
                continue;
            }

            // If existing is VIEW and this one is EDIT, override with the EDIT group info
            if (existing.groupPermission === "VIEW" && group.permission === "EDIT") {
                map.set(gpsId, {
                    ...existing,
                    groupName: group.groupName,
                    groupHomeroomTeacher: group.teacherName,
                    isShared: true,
                    groupPermission: "EDIT",
                    groupType: group.groupType,
                    groupId: group.groupId,
                    groupGradingProfileStatus: groupGradingProfile?.status,
                    gradingProfileId: group.gradingProfileId
                });
            }
        }
    }

    return Array.from(map.values()).sort((a, b) => {
        const last = a.lastName?.localeCompare?.(b.lastName) ?? 0;
        if (last !== 0) return last;
        return a.firstName?.localeCompare?.(b.firstName) ?? 0;
    });
}
